import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import usePatientOrderHistory from '@/lib/api/med-requests/order-history'
import http from '@/lib/http'
import { useLinkedPatients } from '@/services/linkedPatients'
import type { MedRequest } from '@/types/data'

/**
 * A single entry of the patient files feed (`GET /patients/:hash/files`) —
 * the patient's own + linked-group active files, newest first.
 * `url` is a 300 s presigned GET — refetch the feed before opening a file.
 */
export type PatientFile = {
  id: string
  patientHash: string
  originalFilename: string | null
  mimeType: string
  sizeBytes: number | null
  /** Order the file was uploaded for; null = broad patient-level file. */
  orderNumber: string | null
  uploadedBy: 'patient' | 'cs' | 'system'
  viewedByPrescriber: boolean
  createdAt: string
  url: string
}

type PatientFilesFeed = {
  patient_hash: string
  files: PatientFile[]
}

const patientFilesQueryKey = (patientHash: string | undefined) => ['patientFiles', patientHash]

export const usePatientFiles = (patientHash: string | undefined) => {
  return useQuery<PatientFile[]>({
    queryKey: patientFilesQueryKey(patientHash),
    queryFn: async () => ((await http.get(`/patients/${patientHash}/files`)) as unknown as PatientFilesFeed).files,
    enabled: !!patientHash,
    // Presigned URLs expire after 300 s and thumbnails render them directly —
    // refetch on focus (off in the global defaults) and on an interval below
    // the expiry window. Pauses in background tabs.
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 4 * 60 * 1000
  })
}

export const useMarkPatientFileViewed = (patientHash: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (fileId: string) => http.patch(`/patients/${patientHash}/files/${fileId}/viewed`),
    onSuccess: (_data, fileId) => {
      // Flip the flag in the cache so the approval gate opens immediately —
      // no refetch, which would also re-presign every thumbnail URL.
      queryClient.setQueryData<PatientFile[]>(patientFilesQueryKey(patientHash), files =>
        files?.map(file => (file.id === fileId ? { ...file, viewedByPrescriber: true } : file))
      )
    },
    onError: () => {
      // A 404 means the file was deleted or repointed since the feed loaded — resync.
      queryClient.invalidateQueries({ queryKey: patientFilesQueryKey(patientHash) })
    }
  })
}

/**
 * Resolves feed `orderNumber`s to med-request pages (`/request/:id`). The feed
 * carries only `orderNumber` — the request id comes from the patient's (and
 * linked group's) order history, the same queries Previous Orders runs, so they
 * share the react-query cache. Orders that resolve to no request stay unlinked.
 */
export const useOrderRequestIdMap = (patientHash: string | undefined, linkedPatientHash?: string | null) => {
  const { data: ownHistory } = usePatientOrderHistory(patientHash ?? '', !!patientHash)
  const { data: linkedData } = useLinkedPatients(linkedPatientHash)

  const linkedOthers = useMemo(
    () => linkedData?.patients?.filter(patient => patient.hash !== patientHash) ?? [],
    [linkedData, patientHash]
  )

  // Same key/shape as the per-patient history query — cache shared with Previous Orders.
  const linkedHistories = useQueries({
    queries: linkedOthers.map(patient => ({
      queryKey: ['patientOrderHistory', patient.hash],
      queryFn: async (): Promise<MedRequest[]> => http.get(`/prescriber/med-requests/history/${patient.hash}`),
      enabled: !!patient.hash
    }))
  })

  return useMemo(() => {
    const map = new Map<string, string>()
    for (const requests of [ownHistory ?? [], ...linkedHistories.map(query => query.data ?? [])]) {
      for (const request of requests) {
        if (request.orderNumber && !map.has(request.orderNumber)) map.set(request.orderNumber, request.id)
      }
    }
    return map
  }, [ownHistory, linkedHistories])
}

/**
 * Prescriber review gate: Approve / Follow Up / Reject stay disabled until every
 * file in the patient's feed is viewed. Fail-closed — while the feed is loading
 * or errored the gate blocks (files unknown ≠ files viewed). Zero files → open.
 * A request with no patient hash never fetches the feed and reports an error.
 */
export const usePatientFilesGate = (patientHash: string | undefined) => {
  const query = usePatientFiles(patientHash)
  const files = query.data
  const isPending = !!patientHash && query.isPending
  const isError = !patientHash || query.isError

  const unviewedCount = files?.filter(file => !file.viewedByPrescriber).length ?? 0

  return {
    isBlocked: isPending || isError || unviewedCount > 0,
    isPending,
    isError,
    unviewedCount
  }
}
